import React, { useState } from 'react';
import { Project } from '../../types/project';
import { Persona } from '../../types/persona';
import { usePersonas } from '../../hooks/usePersonas';
import { PersonaGenerator } from './PersonaGenerator';
import { PersonaList } from './PersonaList';
import { PersonaStats } from './PersonaStats';
import { PersonaDetailModal } from './PersonaDetailModal';

interface PersonasTabProps {
  projectId: string;
  project: Project;
}

export const PersonasTab: React.FC<PersonasTabProps> = ({ projectId, project }) => {
  const { personas, loading, error } = usePersonas(projectId);
  const [selectedPersona, setSelectedPersona] = useState<Persona | null>(null);
  const [showGenerator, setShowGenerator] = useState(false);

  const hasPersonas = personas.length > 0;

  // Loading state
  if (loading && !hasPersonas) {
    return (
      <div className="py-12 text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        <p className="mt-4 text-gray-600">Loading personas...</p>
      </div>
    );
  }

  // Error state
  if (error && !hasPersonas) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <svg
          className="mx-auto h-12 w-12 text-red-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h3 className="mt-2 text-lg font-medium text-red-900">Error Loading Personas</h3>
        <p className="mt-1 text-sm text-red-700">{error.message}</p>
      </div>
    );
  }

  // No personas yet - show generator
  if (!hasPersonas && !showGenerator) {
    return (
      <div>
        <div className="text-center py-12">
          <svg
            className="mx-auto h-16 w-16 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
          <h3 className="mt-4 text-xl font-semibold text-gray-900">No Personas Yet</h3>
          <p className="mt-2 text-gray-600 max-w-md mx-auto">
            Generate AI-powered consumer personas to understand your target audience better.
            Personas include detailed demographics, cognitive profiles, and brand relationships.
          </p>
          <div className="mt-8">
            <button
              onClick={() => setShowGenerator(true)}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-500 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Generate Personas
            </button>
          </div>
        </div>

        {/* Info Cards */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-primary-50 rounded-lg p-6">
            <div className="flex items-center mb-3">
              <div className="bg-primary-100 rounded-lg p-2">
                <svg
                  className="w-5 h-5 text-primary-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h4 className="ml-3 font-semibold text-indigo-900">Demographics</h4>
            </div>
            <p className="text-sm text-primary-700">
              Age, location, occupation, income, family status, and cultural background
            </p>
          </div>

          <div className="bg-purple-50 rounded-lg p-6">
            <div className="flex items-center mb-3">
              <div className="bg-secondary-100 rounded-lg p-2">
                <svg
                  className="w-5 h-5 text-secondary-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
              </div>
              <h4 className="ml-3 font-semibold text-purple-900">Cognitive Profile</h4>
            </div>
            <p className="text-sm text-purple-700">
              IQ, critical thinking, knowledge levels, media consumption, and awareness
            </p>
          </div>

          <div className="bg-green-50 rounded-lg p-6">
            <div className="flex items-center mb-3">
              <div className="bg-green-100 rounded-lg p-2">
                <svg
                  className="w-5 h-5 text-accent-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
              </div>
              <h4 className="ml-3 font-semibold text-green-900">Psychographics</h4>
            </div>
            <p className="text-sm text-green-700">
              Values, personality traits, goals, and brand relationships
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show generator
  if (showGenerator && !hasPersonas) {
    return (
      <div>
        <div className="mb-4">
          <button
            onClick={() => setShowGenerator(false)}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back
          </button>
        </div>
        <PersonaGenerator
          projectId={projectId}
          onComplete={() => {
            setShowGenerator(false);
          }}
        />
      </div>
    );
  }

  // Has personas - show list and stats
  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Personas</h2>
          <p className="mt-1 text-sm text-gray-600">
            {personas.length} persona{personas.length !== 1 ? 's' : ''} in this project
          </p>
        </div>
        <button
          onClick={() => setShowGenerator(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-500 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          Generate More
        </button>
      </div>

      {/* Stats Overview */}
      <PersonaStats personas={personas} />

      {/* Persona List */}
      <PersonaList
        personas={personas}
        onPersonaClick={(persona) => setSelectedPersona(persona)}
        loading={loading}
      />

      {/* Detail Modal */}
      {selectedPersona && (
        <PersonaDetailModal
          persona={selectedPersona}
          onClose={() => setSelectedPersona(null)}
          onDelete={() => setSelectedPersona(null)}
        />
      )}

      {/* Generator Modal (when adding more) */}
      {showGenerator && hasPersonas && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => setShowGenerator(false)}
            />
            <div className="inline-block w-full max-w-3xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
              <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Generate Additional Personas</h3>
                <button
                  onClick={() => setShowGenerator(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="p-6">
                <PersonaGenerator
                  projectId={projectId}
                  onComplete={() => setShowGenerator(false)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
